import React from 'react';
import { Theme } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import Typography from '@mui/material/Typography';
import { getPricingString } from 'common/utils/common';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { getDurationTypeFromTimePeriod, getLabelForDurationWithPrice } from 'common/utils/pricing';
import Box from '@mui/material/Box';
import { DurationWithPrice, Languages, CurrencyObject, DateFormatObject } from 'common/types';
import { localFormat } from 'common/utils/dateUtils';
import { TFunction } from 'i18next';

interface OwnProps {
	lang: Languages;
	text: {
		label?: string;
		helperText?: string;
		shopClosing: string;
	};
	options: DurationWithPrice[];
	currency: CurrencyObject;
	shopDateFormat: DateFormatObject;
	t: TFunction;
	noPrice?: true;
	showPriceChange?: true;
	currentPrice?: number;
	onValueChange: (durationOption: DurationWithPrice | null) => void;
	value: number;
	disabled?: boolean;
}

type Props = OwnProps & TextFieldProps & WithStyles<typeof styles>;

const DurationDropdown = (props: Props) => {
	const {
		text,
		options,
		noPrice,
		showPriceChange,
		currentPrice,
		classes,
		onValueChange,
		onChange,
		currency,
		shopDateFormat,
		value,
		disabled,
		lang,
		t,
		...otherProps
	} = props;

	const handleDurationChange = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const index = Number(event.target.value);
		const selectedOption = props.options[index] || null;
		onValueChange(selectedOption);
	};

	return (
		<TextField
			{...otherProps}
			id="duration"
			name="duration"
			label={text.label}
			select
			className={classes.textField}
			margin={otherProps.margin ?? 'normal'}
			type="text"
			helperText={text.helperText}
			value={value !== -1 ? value : ''} // Empty here is to indicate empty value of dropdown, required by MaterialUI
			onChange={handleDurationChange}
			disabled={disabled}
		>
			{options.map((option, index) => {
				const durationType = getDurationTypeFromTimePeriod(option.timePeriod);
				const priceChange = props.currentPrice ? option.price - props.currentPrice : 0;
				const customLabel = getLabelForDurationWithPrice(option, lang, t);
				return (
					<MenuItem key={option.durationInSeconds + index} value={index} disabled={option.disabled}>
						<div className={classes.optionWrapper}>
							<div className={classes.primaryOption}>
								<Typography component="div">
									<Box lineHeight="normal">{customLabel}</Box>
								</Typography>
								{durationType === 'opening_hours' ? (
									<Typography variant="caption" color="textSecondary" component="div">
										<Box lineHeight="normal">
											{text.shopClosing}{' '}
											{option.endTime
												? `(${localFormat(option.endTime, 'ddd HH:mm', shopDateFormat)})`
												: ''}
										</Box>
									</Typography>
								) : null}
							</div>
							{!props.noPrice && !props.showPriceChange ? (
								<Typography className={classes.secondaryOption} component="div">
									<Box lineHeight="normal">
										<b>{getPricingString(option.price, currency)}</b>
									</Box>
								</Typography>
							) : props.showPriceChange && priceChange ? (
								<Typography className={classes.secondaryOption} component="div">
									<Box lineHeight="normal">
										<b>
											{(priceChange > 0 ? '+ ' : '- ') +
												getPricingString(Math.abs(priceChange), currency)}
										</b>
									</Box>
								</Typography>
							) : null}
						</div>
					</MenuItem>
				);
			})}
		</TextField>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		textField: {
			width: '100%',
		},
		optionWrapper: {
			width: '100%',
			display: 'flex',
			justifyContent: 'space-between',
			lineHeight: 1,
		},
		primaryOption: {
			flex: 1,
		},
		secondaryOption: {
			textAlign: 'right',
			fontWeight: 400,
		},
	});

export default withStyles(styles)(DurationDropdown);
